import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, Edit, CheckCircle, Send, XCircle, Trash2, Clock, CreditCard,
  Copy, ExternalLink, AlertTriangle, IndianRupee, Calendar, User, FileText,
  Download, Scissors, Undo2,
} from 'lucide-react'
import {
  getInvoice, approveInvoice, sendInvoice, revertInvoice, cancelInvoice, deleteInvoice,
  generateRazorpayLink, recordPayment, downloadInvoicePdf, roundOffInvoice,
  voidPayment, reverseInvoiceAdjustment,
} from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { formatCurrency, formatDate } from '../lib/formatters'
import StatusBadge from '../components/ui/StatusBadge'
import Avatar from '../components/ui/Avatar'
import ProgressBar from '../components/ui/ProgressBar'
import Tooltip from '../components/ui/Tooltip'
import { useUserMode } from '../contexts/UserModeContext'

/* ── Confirmation Dialog ─────────────────────────────────────────── */
function ConfirmDialog({ open, title, description, confirmLabel, confirmClass, onConfirm, onCancel, children }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4 space-y-4 animate-in fade-in">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
        {children}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
          <button type="button" onClick={onConfirm} className={confirmClass || 'btn-primary'}>{confirmLabel || 'Confirm'}</button>
        </div>
      </div>
    </div>
  )
}

// Mirrors MIN_OVERRIDE_REASON in backend-books/src/routes/invoices.js.
const MIN_OVERRIDE_REASON = 10

/* ── Timeline ────────────────────────────────────────────────────── */
function InvoiceTimeline({ invoice }) {
  const events = []

  if (invoice.createdAt) {
    events.push({
      label: 'Created',
      date: invoice.createdAt,
      by: invoice.createdBy?.name,
      icon: FileText,
      color: 'text-gray-400 bg-gray-100',
    })
  }
  if (invoice.approvedAt) {
    events.push({
      label: 'Approved',
      date: invoice.approvedAt,
      by: invoice.approvedBy?.name,
      icon: CheckCircle,
      color: 'text-blue-500 bg-blue-50',
    })
  }
  if (invoice.status === 'SENT' || invoice.status === 'PARTIAL' || invoice.status === 'PAID' || invoice.status === 'OVERDUE') {
    events.push({
      label: 'Sent',
      date: invoice.sentAt || invoice.approvedAt, // fallback if sentAt isn't tracked
      icon: Send,
      color: 'text-indigo-500 bg-indigo-50',
    })
  }
  if (invoice.payments?.length > 0) {
    invoice.payments.forEach((p) => {
      events.push({
        label: `Payment received`,
        detail: formatCurrency(p.amount, invoice.currency),
        date: p.date,
        by: p.recordedBy?.name,
        icon: CreditCard,
        color: 'text-emerald-500 bg-emerald-50',
      })
    })
  }
  if (invoice.status === 'PAID') {
    events.push({
      label: 'Fully Paid',
      date: invoice.payments?.[invoice.payments.length - 1]?.date || invoice.dueDate,
      icon: CheckCircle,
      color: 'text-emerald-600 bg-emerald-100',
    })
  }
  if (invoice.status === 'OVERDUE') {
    events.push({
      label: 'Overdue',
      date: invoice.dueDate,
      icon: AlertTriangle,
      color: 'text-red-500 bg-red-50',
    })
  }
  if (invoice.status === 'CANCELLED') {
    events.push({
      label: 'Cancelled',
      date: invoice.cancelledAt || invoice.updatedAt,
      icon: XCircle,
      color: 'text-gray-500 bg-gray-100',
    })
  }

  // Sort chronologically
  events.sort((a, b) => new Date(a.date) - new Date(b.date))

  if (events.length === 0) return null

  return (
    <div className="card">
      <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Timeline</h2>
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-4 top-2 bottom-2 w-px bg-gray-200" />
        <div className="space-y-4">
          {events.map((ev, i) => {
            const Icon = ev.icon
            return (
              <div key={i} className="relative flex items-start gap-3 pl-1">
                <div className={`relative z-10 flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${ev.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{ev.label}</span>
                    {ev.detail && <span className="text-sm font-semibold text-emerald-700">{ev.detail}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                    <span>{formatDate(ev.date)}</span>
                    {ev.by && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span>{ev.by}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/* ── Main Component ──────────────────────────────────────────────── */
export default function InvoiceDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAccountant } = useUserMode()
  const { isAdmin, isInvoiceCreator } = useAuth()
  const [invoice, setInvoice] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('BANK_TRANSFER')
  const [paymentRef, setPaymentRef] = useState('')
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [showPayment, setShowPayment] = useState(false)
  const [showRoundOff, setShowRoundOff] = useState(false)
  const [roundOffAmount, setRoundOffAmount] = useState('')
  const [roundOffNote, setRoundOffNote] = useState('')
  const [copied, setCopied] = useState(false)
  // Reversing a payment and reversing a round-off ask the same question — which
  // row, and why — so they share one dialog. { kind: 'payment' | 'adjustment' }
  const [reverse, setReverse] = useState(null)
  const [reverseReason, setReverseReason] = useState('')
  const [reversing, setReversing] = useState(false)

  // Confirmation dialog state
  const [confirm, setConfirm] = useState({ open: false, title: '', description: '', confirmLabel: '', confirmClass: '', onConfirm: () => {}, extra: null })
  const [cancelReason, setCancelReason] = useState('')
  const cancelReasonRef = useRef('')
  const [posOverrideReason, setPosOverrideReason] = useState('')
  const [posOverrideTouched, setPosOverrideTouched] = useState(false)
  const posOverrideReasonRef = useRef('')

  const load = () => {
    setLoading(true)
    getInvoice(id)
      .then((res) => setInvoice(res.data))
      .catch(() => navigate('/invoices'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [id])

  const action = async (fn, ...args) => {
    setActionLoading(fn.name || 'loading')
    try {
      const res = await fn(...args)
      if (res?.data?.driveWarning) alert(res.data.driveWarning)
      load()
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed')
    } finally {
      setActionLoading('')
    }
  }

  // Reversing is admin-only and refused server-side on a cancelled or draft
  // invoice, so the control is not offered there either.
  const canReverse = isAdmin && !['CANCELLED', 'DRAFT'].includes(invoice?.status)

  const handleReverse = async (e) => {
    e.preventDefault()
    setReversing(true)
    try {
      if (reverse.kind === 'payment') {
        await voidPayment(reverse.row.id, { reason: reverseReason })
      } else {
        await reverseInvoiceAdjustment(id, reverse.row.id, { reason: reverseReason })
      }
      setReverse(null)
      setReverseReason('')
      load()
    } catch (err) {
      alert(err.response?.data?.error || 'Could not reverse that entry')
    } finally {
      setReversing(false)
    }
  }

  const handleRecordPayment = async (e) => {
    e.preventDefault()
    await action(recordPayment, {
      invoiceId: id,
      date: paymentDate,
      amount: Number(paymentAmount),
      method: paymentMethod,
      referenceNo: paymentRef || undefined,
    })
    setShowPayment(false)
    setPaymentAmount('')
    setPaymentRef('')
    setPaymentDate(new Date().toISOString().slice(0, 10))
  }

  const openConfirm = (opts) => setConfirm({ open: true, ...opts })
  const closeConfirm = () => {
    setConfirm((c) => ({ ...c, open: false }))
    setCancelReason(''); cancelReasonRef.current = ''
    setPosOverrideReason(''); posOverrideReasonRef.current = ''
    setPosOverrideTouched(false)
  }

  // Approving is refused when the place of supply disagrees with the customer's own
  // state — the defect behind O3D-2627-0176. Almost always the place of supply is simply
  // wrong and belongs back in the invoice; occasionally the supply genuinely terminates
  // elsewhere (s.10(1)(a)), and then the reason typed here is the only record of that.
  const handleApprove = async (overrideReason) => {
    setActionLoading('approveInvoice')
    try {
      const res = await approveInvoice(id, overrideReason ? { placeOfSupplyOverrideReason: overrideReason } : undefined)
      if (res?.data?.driveWarning) alert(res.data.driveWarning)
      load()
    } catch (err) {
      const data = err.response?.data
      if (data?.code === 'PLACE_OF_SUPPLY_CONFLICT') {
        setPosOverrideReason(''); posOverrideReasonRef.current = ''
        setPosOverrideTouched(false)
        openConfirm({
          title: 'Place of supply does not match the customer',
          description: data.error,
          confirmLabel: 'Approve anyway',
          confirmClass: 'btn-danger',
          onConfirm: () => {
            // Held here rather than sent and bounced. Round-tripping a short reason meant
            // closeConfirm() had already wiped the box by the time the server answered, so
            // the user lost what they had typed and got a bare alert. The server enforces
            // the same minimum independently; this only spares the trip.
            const reason = posOverrideReasonRef.current.trim()
            if (reason.length < MIN_OVERRIDE_REASON) { setPosOverrideTouched(true); return }
            closeConfirm()
            handleApprove(reason)
          },
          extra: 'posOverride',
        })
        return
      }
      alert(data?.error || 'Action failed')
    } finally {
      setActionLoading('')
    }
  }

  const copyLink = (url) => {
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  /* ── Loading skeleton ────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-40 bg-gray-100 rounded-xl" />
        <div className="h-64 bg-gray-100 rounded-xl" />
      </div>
    )
  }

  if (!invoice) return null

  const inv = invoice
  const isDraft = inv.status === 'DRAFT'
  const canSend = ['APPROVED', 'SENT', 'PARTIAL', 'OVERDUE'].includes(inv.status)
  const canPay = ['APPROVED', 'SENT', 'PARTIAL', 'OVERDUE'].includes(inv.status) && inv.balanceDue > 0
  const canCancel = ['APPROVED', 'SENT', 'OVERDUE', 'PARTIAL'].includes(inv.status)
  const paidAmount = Number(inv.total) - Number(inv.balanceDue)
  const totalTax = Number(inv.cgst || 0) + Number(inv.sgst || 0) + Number(inv.igst || 0)

  // ── Restricted invoice-creator view: NO financial figures (amounts, tax, balance,
  // payments). Only status, customer, line-item descriptions, and Download PDF. ──
  if (isInvoiceCreator) {
    const downloadPdf = async () => {
      setActionLoading('pdf')
      try {
        const res = await downloadInvoicePdf(id)
        const url = window.URL.createObjectURL(res.data)
        const a = document.createElement('a')
        a.href = url
        a.download = `${inv.number}.pdf`
        a.click()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        alert(err.response?.data?.error || 'Failed to generate PDF')
      } finally {
        setActionLoading('')
      }
    }
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/invoices" className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-xl font-semibold text-gray-900">{inv.number || 'DRAFT'}</h1>
          <StatusBadge status={inv.status} />
        </div>

        <div className="card space-y-4">
          <div className="flex items-center gap-2">
            <Avatar name={inv.customer?.name} size="md" />
            <div>
              <p className="font-medium text-gray-900">{inv.billToName || inv.customer?.name || 'No customer'}</p>
              <p className="text-xs text-gray-500">{formatDate(inv.date)}</p>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Items</p>
            <ul className="space-y-1.5">
              {(inv.lineItems || []).map((li) => (
                <li key={li.id} className="text-sm text-gray-700 flex justify-between gap-3 border-b border-gray-100 pb-1.5">
                  <span className="min-w-0">{li.description}</span>
                  <span className="text-gray-400 flex-shrink-0">Qty {li.qty}</span>
                </li>
              ))}
            </ul>
          </div>

          {inv.customerNote && (
            <p className="text-xs text-gray-500 whitespace-pre-line">{inv.customerNote}</p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {isDraft ? (
            <Link to={`/invoices/${id}/edit`} className="btn-secondary flex items-center gap-1.5">
              <Edit className="h-4 w-4" /> Edit
            </Link>
          ) : (
            <button onClick={downloadPdf} disabled={!!actionLoading} className="btn-primary flex items-center gap-1.5">
              <Download className="h-4 w-4" /> {actionLoading === 'pdf' ? 'Generating...' : 'Download PDF'}
            </button>
          )}
        </div>

        {isDraft && (
          <p className="text-xs text-gray-400">This invoice is awaiting approval. You'll be able to download the PDF once it's approved.</p>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* ── Back + Title ─────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <Link to="/invoices" className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-xl font-semibold text-gray-900">{inv.number || 'DRAFT'}</h1>
      </div>

      {/* ── Summary Card ─────────────────────────────────────────── */}
      <div className="card bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          {/* Left: Amount + status */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <StatusBadge status={inv.status} size="lg" />
              {inv.status === 'OVERDUE' && inv.dueDate && (
                <span className="text-xs font-medium text-red-500">
                  {Math.floor((Date.now() - new Date(inv.dueDate).getTime()) / 86400000)} days overdue
                </span>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Amount</p>
              <p className="text-3xl font-bold text-gray-900 tracking-tight">{formatCurrency(inv.total, inv.currency)}</p>
            </div>
            {inv.balanceDue > 0 && inv.balanceDue < inv.total && (
              <p className="text-sm text-gray-500">
                Balance due: <span className="font-semibold text-red-600">{formatCurrency(inv.balanceDue, inv.currency)}</span>
              </p>
            )}
          </div>

          {/* Right: Customer + dates */}
          <div className="flex flex-col items-start md:items-end gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Avatar name={inv.customer?.name} size="md" />
              <div className="text-right">
                <p className="font-medium text-gray-900">{inv.billToName || inv.customer?.name || 'No customer'}</p>
                {inv.billToAddress && <p className="text-xs text-gray-500 whitespace-pre-line">{inv.billToAddress}</p>}
                {!inv.billToAddress && inv.customer?.email && <p className="text-xs text-gray-500">{inv.customer.email}</p>}
              </div>
            </div>
            <div className="flex items-center gap-4 text-gray-500">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                <span>{formatDate(inv.date)}</span>
              </div>
              <span className="text-gray-300">to</span>
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                <span>{formatDate(inv.dueDate)}</span>
              </div>
            </div>
            {isAccountant && inv.placeOfSupply && (
              <p className="text-xs text-gray-400">Place of Supply: {inv.placeOfSupply}</p>
            )}
          </div>
        </div>

        {/* Payment progress */}
        {inv.total > 0 && inv.status !== 'DRAFT' && inv.status !== 'CANCELLED' && (
          <div className="mt-5 pt-5 border-t border-gray-200">
            <ProgressBar paid={paidAmount} total={Number(inv.total)} />
          </div>
        )}
      </div>

      {/* ── Actions Bar ──────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        {!isDraft && (
          <button
            onClick={async () => {
              setActionLoading('pdf')
              try {
                const res = await downloadInvoicePdf(id)
                const url = window.URL.createObjectURL(res.data)
                const a = document.createElement('a')
                a.href = url
                a.download = `${inv.number}.pdf`
                a.click()
                window.URL.revokeObjectURL(url)
              } catch (err) {
                alert(err.response?.data?.error || 'Failed to generate PDF')
              } finally {
                setActionLoading('')
              }
            }}
            disabled={!!actionLoading}
            className="btn-secondary flex items-center gap-1.5"
          >
            <Download className="h-4 w-4" /> {actionLoading === 'pdf' ? 'Generating...' : 'Download PDF'}
          </button>
        )}
        {isDraft && (
          <>
            <Link to={`/invoices/${id}/edit`} className="btn-secondary flex items-center gap-1.5">
              <Edit className="h-4 w-4" /> Edit
            </Link>
            <button
              onClick={() => openConfirm({
                title: 'Approve Invoice',
                description: 'Approving this invoice locks it for editing. The invoice number will be assigned and it will be ready to send to the customer. This action cannot be undone.',
                confirmLabel: 'Approve',
                onConfirm: () => { closeConfirm(); handleApprove() },
              })}
              disabled={!!actionLoading}
              className="btn-primary flex items-center gap-1.5"
            >
              <CheckCircle className="h-4 w-4" /> Approve
            </button>
            <button
              onClick={() => openConfirm({
                title: 'Delete Draft',
                description: 'This will permanently delete this draft invoice and all its line items. This cannot be undone.',
                confirmLabel: 'Delete',
                confirmClass: 'btn-danger',
                onConfirm: () => { closeConfirm(); action(deleteInvoice, id).then(() => navigate('/invoices')) },
              })}
              disabled={!!actionLoading}
              className="btn-danger flex items-center gap-1.5"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </>
        )}
        {inv.status === 'APPROVED' && (
          <button
            onClick={() => openConfirm({
              title: 'Revert to Draft',
              description: 'This will revert the invoice back to draft status. The invoice number will be released and can be reassigned. Use this if you need to make changes before sending.',
              confirmLabel: 'Revert to Draft',
              confirmClass: 'btn-secondary',
              onConfirm: async () => { await action(revertInvoice, id); closeConfirm() },
            })}
            disabled={!!actionLoading}
            className="btn-ghost flex items-center gap-1.5 text-amber-600 hover:text-amber-700"
          >
            <Edit className="h-4 w-4" /> Revert to Draft
          </button>
        )}
        {canSend && inv.status !== 'SENT' && (
          <button
            onClick={() => action(sendInvoice, id)}
            disabled={!!actionLoading}
            className="btn-secondary flex items-center gap-1.5"
          >
            <Send className="h-4 w-4" /> {actionLoading === 'loading' ? 'Sending...' : 'Mark as Sent'}
          </button>
        )}
        {canPay && (
          <>
            <button onClick={() => setShowPayment(true)} className="btn-primary flex items-center gap-1.5">
              <CreditCard className="h-4 w-4" /> Record Payment
            </button>
            <button
              onClick={() => action(generateRazorpayLink, id)}
              disabled={!!actionLoading}
              className="btn-secondary flex items-center gap-1.5"
            >
              <IndianRupee className="h-4 w-4" /> Razorpay Link
            </button>
          </>
        )}
        {inv.status === 'PARTIAL' && isAdmin && (
          <button
            onClick={() => { setRoundOffAmount(String(inv.balanceDue)); setRoundOffNote(''); setShowRoundOff(true) }}
            className="btn-secondary flex items-center gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
          >
            <Scissors className="h-4 w-4" /> Round Off
          </button>
        )}
        {canCancel && (
          <button
            onClick={() => {
              setCancelReason('')
              cancelReasonRef.current = ''
              openConfirm({
                title: 'Cancel Invoice',
                description: 'Cancelling this invoice will void it. The customer will no longer be able to pay against it. You may need to issue a credit note if payments have already been received.',
                confirmLabel: 'Cancel Invoice',
                confirmClass: 'btn-danger',
                onConfirm: () => {
                  if (!cancelReasonRef.current.trim()) return
                  const reason = cancelReasonRef.current
                  closeConfirm()
                  action(cancelInvoice, id, reason)
                },
                extra: 'cancelReason',
              })
            }}
            disabled={!!actionLoading}
            className="btn-danger flex items-center gap-1.5"
          >
            <XCircle className="h-4 w-4" /> Cancel
          </button>
        )}
      </div>

      {/* ── Razorpay Link Banner ─────────────────────────────────── */}
      {inv.razorpayLinkUrl && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-3 text-sm">
          <IndianRupee className="h-4 w-4 text-blue-600 flex-shrink-0" />
          <span className="text-blue-800 font-medium">Payment Link:</span>
          <a href={inv.razorpayLinkUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline truncate flex-1">
            {inv.razorpayLinkUrl}
          </a>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={() => copyLink(inv.razorpayLinkUrl)} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-xs font-medium">
              <Copy className="h-3.5 w-3.5" /> {copied ? 'Copied!' : 'Copy'}
            </button>
            <a href={inv.razorpayLinkUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      )}

      {/* ── Two Column: Line Items + Timeline ────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Line Items (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card overflow-hidden">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Line Items</h2>
            <div className="overflow-x-auto -mx-4 sm:-mx-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-2.5 font-medium">Description</th>
                    {isAccountant && <th className="px-4 py-2.5 font-medium">HSN/SAC</th>}
                    <th className="px-4 py-2.5 font-medium text-right">Qty</th>
                    <th className="px-4 py-2.5 font-medium text-right">Rate</th>
                    {isAccountant ? (
                      <>
                        <th className="px-4 py-2.5 font-medium text-right">GST %</th>
                        <th className="px-4 py-2.5 font-medium text-right">CGST</th>
                        <th className="px-4 py-2.5 font-medium text-right">SGST</th>
                        <th className="px-4 py-2.5 font-medium text-right">IGST</th>
                      </>
                    ) : (
                      <th className="px-4 py-2.5 font-medium text-right">Tax</th>
                    )}
                    <th className="px-4 py-2.5 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {inv.lineItems?.map((li, i) => {
                    const lineTax = Number(li.cgst || 0) + Number(li.sgst || 0) + Number(li.igst || 0)
                    return (
                      <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-gray-900">{li.description}</div>
                          {li.detailedDescription && (
                            <div className="text-xs text-gray-500 mt-0.5 whitespace-pre-wrap">{li.detailedDescription}</div>
                          )}
                        </td>
                        {isAccountant && <td className="px-4 py-2.5 text-gray-500">{li.hsnSacCode || '—'}</td>}
                        <td className="px-4 py-2.5 text-right text-gray-700">{Number(li.qty)}</td>
                        <td className="px-4 py-2.5 text-right text-gray-700">{formatCurrency(li.rate, inv.currency)}</td>
                        {isAccountant ? (
                          <>
                            <td className="px-4 py-2.5 text-right text-gray-500">{Number(li.gstRate)}%</td>
                            <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(li.cgst || 0, inv.currency)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(li.sgst || 0, inv.currency)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(li.igst || 0, inv.currency)}</td>
                          </>
                        ) : (
                          <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(lineTax, inv.currency)}</td>
                        )}
                        <td className="px-4 py-2.5 text-right font-semibold text-gray-900">{formatCurrency(li.total, inv.currency)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
              <div className="w-72 space-y-2 text-sm">
                <div className="flex justify-between text-gray-500">
                  <span>Subtotal</span>
                  <span className="text-gray-900">{formatCurrency(inv.subtotal, inv.currency)}</span>
                </div>
                {isAccountant ? (
                  <>
                    {Number(inv.cgst) > 0 && (
                      <div className="flex justify-between text-gray-500">
                        <span>CGST</span>
                        <span>{formatCurrency(inv.cgst, inv.currency)}</span>
                      </div>
                    )}
                    {Number(inv.sgst) > 0 && (
                      <div className="flex justify-between text-gray-500">
                        <span>SGST</span>
                        <span>{formatCurrency(inv.sgst, inv.currency)}</span>
                      </div>
                    )}
                    {Number(inv.igst) > 0 && (
                      <div className="flex justify-between text-gray-500">
                        <span>IGST</span>
                        <span>{formatCurrency(inv.igst, inv.currency)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  totalTax > 0 && (
                    <div className="flex justify-between text-gray-500">
                      <Tooltip text="Includes CGST, SGST, or IGST as applicable">
                        <span>Tax</span>
                      </Tooltip>
                      <span>{formatCurrency(totalTax, inv.currency)}</span>
                    </div>
                  )
                )}
                <div className="flex justify-between font-semibold text-base border-t border-gray-200 pt-2 mt-2">
                  <span className="text-gray-900">Total</span>
                  <span className="text-gray-900">{formatCurrency(inv.total, inv.currency)}</span>
                </div>
                {inv.balanceDue > 0 && inv.balanceDue < inv.total && (
                  <div className="flex justify-between text-red-600 font-medium">
                    <span>Balance Due</span>
                    <span>{formatCurrency(inv.balanceDue, inv.currency)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment History + Round-off Adjustments */}
          {(inv.payments?.length > 0 || inv.adjustments?.length > 0) && (
            <div className="card">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                {inv.adjustments?.length > 0 ? 'Payments & Adjustments' : 'Payments'}
              </h2>
              <div className="overflow-x-auto -mx-4 sm:-mx-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-2.5 font-medium">Date</th>
                      <th className="px-4 py-2.5 font-medium">Type</th>
                      <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                      <th className="px-4 py-2.5 font-medium">Reference / Note</th>
                      <th className="px-4 py-2.5 font-medium">By</th>
                      {canReverse && <th className="px-4 py-2.5 font-medium text-right">Reverse</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {inv.payments.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-2.5">{formatDate(p.date)}</td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                            {p.method?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-emerald-700">{formatCurrency(p.amount, inv.currency)}</td>
                        <td className="px-4 py-2.5 text-gray-500">{p.referenceNo || '—'}</td>
                        <td className="px-4 py-2.5">
                          {p.recordedBy?.name ? (
                            <div className="flex items-center gap-1.5">
                              <Avatar name={p.recordedBy.name} size="sm" />
                              <span className="text-gray-700 text-xs">{p.recordedBy.name}</span>
                            </div>
                          ) : '—'}
                        </td>
                        {canReverse && (
                          <td className="px-4 py-2.5 text-right">
                            <button
                              onClick={() => { setReverseReason(''); setReverse({ kind: 'payment', row: p }) }}
                              className="btn-secondary btn-sm border-rose-300 text-rose-700 hover:bg-rose-50"
                              title="Void this payment — the invoice owes the money again"
                            >
                              Void
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {inv.adjustments?.map((a) => (
                      <tr key={a.id} className={a.reversedAt
                        ? 'bg-gray-50 text-gray-400'
                        : 'bg-amber-50/50 hover:bg-amber-50 transition-colors'}>
                        <td className="px-4 py-2.5">{formatDate(a.date)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            a.reversedAt ? 'bg-gray-100 text-gray-500' : 'bg-amber-100 text-amber-800'
                          }`}>
                            <Scissors className="h-3 w-3" /> Round Off
                          </span>
                        </td>
                        <td className={`px-4 py-2.5 text-right font-semibold ${
                          a.reversedAt ? 'text-gray-400 line-through' : 'text-amber-700'
                        }`}>
                          {formatCurrency(a.amount, inv.currency)}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 italic">{a.note || '—'}</td>
                        <td className="px-4 py-2.5">
                          {a.appliedBy ? (
                            <div className="flex items-center gap-1.5">
                              <Avatar name={a.appliedBy} size="sm" />
                              <span className="text-gray-700 text-xs">{a.appliedBy}</span>
                            </div>
                          ) : '—'}
                        </td>
                        {canReverse && (
                          <td className="px-4 py-2.5 text-right whitespace-nowrap">
                            {a.reversedAt ? (
                              <span className="text-xs text-gray-400">
                                reversed{a.reversedBy ? ` by ${a.reversedBy}` : ''}
                              </span>
                            ) : (
                              <button
                                onClick={() => { setReverseReason(''); setReverse({ kind: 'adjustment', row: a }) }}
                                className="btn-secondary btn-sm border-amber-300 text-amber-700 hover:bg-amber-50"
                                title="Put this amount back on the invoice"
                              >
                                Reverse
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes */}
          {inv.notes && (
            <div className="card">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-2">Notes</h2>
              <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">{inv.notes}</p>
            </div>
          )}

          {/* Meta info (Accountant mode) */}
          {isAccountant && (
            <div className="card bg-gray-50 border-dashed">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Details</h2>
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Currency</dt>
                  <dd className="font-medium text-gray-900">{inv.currency}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Payment Terms</dt>
                  <dd className="font-medium text-gray-900">{inv.paymentTerms || '—'}</dd>
                </div>
                {inv.placeOfSupply && (
                  <div>
                    <dt className="text-gray-500">Place of Supply</dt>
                    <dd className="font-medium text-gray-900">{inv.placeOfSupply}</dd>
                  </div>
                )}
                {inv.createdBy && (
                  <div>
                    <dt className="text-gray-500">Created by</dt>
                    <dd className="font-medium text-gray-900 flex items-center gap-1.5">
                      <Avatar name={inv.createdBy.name} size="sm" />
                      {inv.createdBy.name}
                    </dd>
                  </div>
                )}
                {inv.approvedBy && (
                  <div>
                    <dt className="text-gray-500">Approved by</dt>
                    <dd className="font-medium text-gray-900 flex items-center gap-1.5">
                      <Avatar name={inv.approvedBy.name} size="sm" />
                      {inv.approvedBy.name}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>

        {/* Timeline (1/3 width) */}
        <div className="lg:col-span-1">
          <InvoiceTimeline invoice={inv} />
        </div>
      </div>

      {/* ── Record Payment Dialog ────────────────────────────────── */}
      {showPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <form onSubmit={handleRecordPayment} className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Record Payment</h3>
            <p className="text-sm text-gray-500">
              Recording a payment for <span className="font-medium text-gray-700">{inv.number}</span>.
              Balance due: <span className="font-semibold text-red-600">{formatCurrency(inv.balanceDue, inv.currency)}</span>
            </p>
            <div>
              <label className="label">Payment Date</label>
              <input
                type="date"
                className="input"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                onKeyDown={(e) => e.preventDefault()}
                required
              />
            </div>
            <div>
              <label className="label">Amount</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={inv.balanceDue}
                className="input"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder={`Max ${formatCurrency(inv.balanceDue, inv.currency)}`}
                required
              />
            </div>
            <div>
              <label className="label">Method</label>
              <select className="select" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <option value="BANK_TRANSFER">Bank Transfer</option>
                <option value="UPI">UPI</option>
                <option value="CASH">Cash</option>
                <option value="CHEQUE">Cheque</option>
                <option value="RAZORPAY">Razorpay</option>
              </select>
            </div>
            <div>
              <label className="label">Reference No. <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="text"
                className="input"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                placeholder="Transaction ID, cheque no., etc."
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setShowPayment(false); setPaymentAmount(''); setPaymentRef('') }} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={!!actionLoading} className="btn-primary flex items-center gap-1.5">
                <CreditCard className="h-4 w-4" /> Record Payment
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Round Off Dialog ────────────────────────────────────── */}
      {showRoundOff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              try {
                await roundOffInvoice(id, { amount: Number(roundOffAmount), note: roundOffNote })
                setShowRoundOff(false)
                load()
              } catch (err) {
                alert(err.response?.data?.error || 'Failed to apply round-off')
              }
            }}
            className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4 space-y-4"
          >
            <h3 className="text-lg font-semibold text-gray-900">Apply Round Off</h3>
            <p className="text-sm text-gray-500">
              Closes the residual balance on <span className="font-medium text-gray-700">{inv.number}</span> as an accounting adjustment.
              Balance due: <span className="font-semibold text-amber-600">{formatCurrency(inv.balanceDue, inv.currency)}</span>
            </p>
            <div>
              <label className="label">Amount</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={inv.balanceDue}
                className="input"
                value={roundOffAmount}
                onChange={(e) => setRoundOffAmount(e.target.value)}
                required
              />
            </div>
            {Number(roundOffAmount) > 50 && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Amount exceeds ₹50. Make sure this is intentional before proceeding.</span>
              </div>
            )}
            <div>
              <label className="label">Note <span className="text-red-500">*</span></label>
              <input
                type="text"
                className="input"
                value={roundOffNote}
                onChange={(e) => setRoundOffNote(e.target.value)}
                placeholder="e.g. GST rounding difference"
                required
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowRoundOff(false)} className="btn-secondary">Cancel</button>
              <button type="submit" className="btn-primary flex items-center gap-1.5">
                <Scissors className="h-4 w-4" /> Apply Round Off
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Reverse a payment or a round-off ─────────────────────── */}
      {reverse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <form onSubmit={handleReverse} className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {reverse.kind === 'payment' ? 'Void this payment' : 'Reverse this round off'}
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {formatCurrency(reverse.row.amount, inv.currency)}
              {reverse.kind === 'payment' && reverse.row.referenceNo ? ` · ${reverse.row.referenceNo}` : ''}
              {' '}on <span className="font-medium text-gray-800">{inv.number}</span> is removed and the
              invoice owes that amount again.
              {reverse.kind === 'payment' && ' Any bank statement line this payment came from goes back to “Needs you”.'}
            </p>
            {reverse.kind === 'payment' && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>The payment record is deleted. Only the audit log keeps a copy.</span>
              </div>
            )}
            <div>
              <label className="label">Reason <span className="text-red-500">*</span></label>
              <input
                type="text"
                className="input"
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
                placeholder={reverse.kind === 'payment'
                  ? 'e.g. recorded at the wrong amount — 10 arrived separately'
                  : 'e.g. the 10 was actually received by NEFT on 16 Apr'}
                required
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setReverse(null)} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={reversing || !reverseReason.trim()} className="btn-danger flex items-center gap-1.5">
                <Undo2 className="h-4 w-4" />
                {reversing ? 'Reversing…' : reverse.kind === 'payment' ? 'Void payment' : 'Reverse round off'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Confirmation Dialog ──────────────────────────────────── */}
      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        description={confirm.description}
        confirmLabel={confirm.confirmLabel}
        confirmClass={confirm.confirmClass}
        onConfirm={confirm.onConfirm}
        onCancel={closeConfirm}
      >
        {confirm.extra === 'posOverride' && (
          <div className="mt-3">
            <label className="label">Why does the supply take place there?</label>
            <textarea
              className="input min-h-[80px]"
              value={posOverrideReason}
              onChange={(e) => { setPosOverrideReason(e.target.value); posOverrideReasonRef.current = e.target.value }}
              placeholder="e.g. goods delivered to the buyer's Maharashtra site — s.10(1)(a)"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Recorded against this invoice. If the place of supply is simply wrong, close
              this and correct it on the invoice instead.
            </p>
            {(posOverrideTouched || posOverrideReason.trim().length > 0)
              && posOverrideReason.trim().length < MIN_OVERRIDE_REASON && (
              <p className="mt-1 text-xs text-red-600">
                Say a bit more — this is the whole record of why the tax was charged this way.
              </p>
            )}
          </div>
        )}
        {confirm.extra === 'cancelReason' && (
          <div className="mt-3">
            <label className="label">Cancellation reason</label>
            <textarea
              className="input min-h-[80px]"
              value={cancelReason}
              onChange={(e) => { setCancelReason(e.target.value); cancelReasonRef.current = e.target.value }}
              placeholder="Why is this invoice being cancelled?"
              required
            />
          </div>
        )}
      </ConfirmDialog>
    </div>
  )
}
